import type { SlotPlacement } from "../dashboard/widgets/types";
import type { DateRange, DateRangePreset } from "../client/utils/dateRange";
import { presetToDateRange } from "../client/utils/dateRange";
import type { AccountFilterValue } from "../trading-accounts/AccountGroupFilter";

function findTopbarSlot(slots: SlotPlacement[], widgetKey: string): SlotPlacement | undefined {
  return slots.find((s) => s.placement === "topbar" && s.widgetKey === widgetKey);
}

export function getDefaultDateRange(
  slots: SlotPlacement[],
  fallbackPreset: DateRangePreset = "last_30_days",
): DateRange {
  const slot = findTopbarSlot(slots, "topbar-date-filter");
  if (!slot?.defaultFilterValue) return presetToDateRange(fallbackPreset);
  try {
    const parsed = JSON.parse(slot.defaultFilterValue);
    if (parsed.preset) return presetToDateRange(parsed.preset);
    if (parsed.from) {
      return {
        from: new Date(parsed.from),
        to: parsed.to ? new Date(parsed.to) : null,
      };
    }
  } catch {}
  return presetToDateRange(fallbackPreset);
}

export function getDefaultAccountFilter(slots: SlotPlacement[]): AccountFilterValue {
  const slot = findTopbarSlot(slots, "topbar-account-filter");
  if (!slot?.defaultFilterValue) return { type: "all" };
  try {
    return JSON.parse(slot.defaultFilterValue) as AccountFilterValue;
  } catch {}
  return { type: "all" };
}

export function getDefaultStrategyIds(slots: SlotPlacement[]): string[] {
  const slot = findTopbarSlot(slots, "topbar-strategy-filter");
  if (!slot?.defaultFilterValue) return [];
  try {
    const parsed = JSON.parse(slot.defaultFilterValue);
    if (Array.isArray(parsed)) return parsed as string[];
  } catch {}
  return [];
}

export function serializeDateRange(range: DateRange): string {
  if ((range as any).preset) return JSON.stringify({ preset: (range as any).preset });
  return JSON.stringify({
    from: range.from?.toISOString().split("T")[0] ?? null,
    to: range.to?.toISOString().split("T")[0] ?? null,
  });
}

export function serializeAccountFilter(filter: AccountFilterValue): string {
  return JSON.stringify(filter);
}

export function serializeStrategyIds(ids: string[]): string {
  return JSON.stringify(ids);
}

export function getDefaultPfmFilter(slots: SlotPlacement[]): string | null {
  const slot = findTopbarSlot(slots, "topbar-pfm-filter");
  if (!slot?.defaultFilterValue) return null;
  try {
    const parsed = JSON.parse(slot.defaultFilterValue);
    return typeof parsed === "string" ? parsed : null;
  } catch {}
  return null;
}

export function serializePfmFilter(id: string | null): string {
  return JSON.stringify(id);
}

export function getDefaultPhaseFilter(slots: SlotPlacement[]): string | null {
  const slot = findTopbarSlot(slots, "topbar-phase-filter");
  if (!slot?.defaultFilterValue) return null;
  try {
    const parsed = JSON.parse(slot.defaultFilterValue);
    return typeof parsed === "string" ? parsed : null;
  } catch {}
  return null;
}

export function serializePhaseFilter(status: string | null): string {
  return JSON.stringify(status);
}
